import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Link,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { authService } from '../../services/authService';
import { phoneAuthService } from '../../services/phoneAuthService';
import { PhoneVerification } from '../../components/PhoneVerification';

interface FormData {
    name: string;
    email: string;
    phone: string;
    password: string;
    countryCode: string;
    isCustomCountry: boolean;
}

const countryCodes = [
    { code: '+1', country: 'Amerika' },
    { code: '+49', country: 'Almanya' },
    { code: '+43', country: 'Avusturya' },
    { code: '+994', country: 'Azerbaycan' },
    { code: '+32', country: 'Belçika' },
    { code: '+420', country: 'Çek Cumhuriyeti' },
    { code: '+45', country: 'Danimarka' },
    { code: '+358', country: 'Finlandiya' },
    { code: '+33', country: 'Fransa' },
    { code: '+31', country: 'Hollanda' },
    { code: '+44', country: 'İngiltere' },
    { code: '+39', country: 'İtalya' },
    { code: '+41', country: 'İsviçre' },
    { code: '+46', country: 'İsveç' },
    { code: '+34', country: 'İspanya' },
    { code: '+36', country: 'Macaristan' },
    { code: '+47', country: 'Norveç' },
    { code: '+48', country: 'Polonya' },
    { code: '+351', country: 'Portekiz' },
    { code: '+90', country: 'Türkiye' },
    { code: '+30', country: 'Yunanistan' }
];

const steps = ['Kişisel Bilgiler', 'Telefon Doğrulama'];

export const RegisterForm: React.FC = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        password: '',
        countryCode: '+90',
        isCustomCountry: false
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [verificationId, setVerificationId] = useState<string>('');
    const recaptchaContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let mounted = true;

        const initRecaptcha = async () => {
            try {
                if (!recaptchaContainerRef.current) return;

                // Önceki reCAPTCHA'yı temizle
                phoneAuthService.clearRecaptcha();
                console.log('Previous reCAPTCHA cleared');

                // Yeni reCAPTCHA'yı başlat
                await phoneAuthService.initRecaptcha('recaptcha-container');
                console.log('New reCAPTCHA initialized');
            } catch (err: any) {
                if (mounted) {
                    const errorMessage = err.message || 'reCAPTCHA başlatılamadı';
                    setError(errorMessage);
                }
            }
        };

        // İkinci adıma geçildiğinde reCAPTCHA'yı başlat
        if (activeStep === 1) {
            initRecaptcha();
        }

        return () => {
            mounted = false;
            phoneAuthService.clearRecaptcha();
            console.log('reCAPTCHA cleared on component unmount');
        };
    }, [activeStep]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'phone') {
            processedValue = value.replace(/\D/g, '');
            processedValue = processedValue.slice(0, 10);
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
    };

    const handleCountryChange = (e: any) => {
        setFormData(prev => ({
            ...prev,
            countryCode: e.target.value
        }));
    };

    const handleCustomCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const processedValue = value.replace(/[^0-9+]/g, '');
        
        setFormData(prev => ({
            ...prev,
            countryCode: processedValue
        }));
    };

    const toggleCustomCountry = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            isCustomCountry: e.target.checked,
            countryCode: e.target.checked ? '+' : '+90'
        }));
    };

    const validateFirstStep = () => {
        if (!formData.email || !formData.password || !formData.name) {
            setError('Lütfen tüm alanları doldurun.');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Şifre en az 6 karakter olmalıdır.');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Geçerli bir e-posta adresi girin.');
            return false;
        }

        return true;
    };

    const validateSecondStep = () => {
        if (!formData.phone) {
            setError('Lütfen telefon numaranızı girin.');
            return false;
        }

        if (!formData.countryCode) {
            setError('Lütfen ülke kodunu girin.');
            return false;
        }

        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.phone)) {
            setError('Geçerli bir telefon numarası girin (10 haneli).');
            return false;
        }

        return true;
    };

    const handleFirstStep = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateFirstStep()) {
            return;
        }

        setActiveStep(1);
    };

    const handleSecondStep = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateSecondStep()) {
            return;
        }

        try {
            setLoading(true);

            // reCAPTCHA kontrolü
            if (!window.recaptchaVerifier) {
                throw new Error('reCAPTCHA doğrulaması gerekli. Lütfen sayfayı yenileyin.');
            }

            const fullPhoneNumber = `${formData.countryCode}${formData.phone}`;
            const verificationId = await phoneAuthService.sendVerificationCode(fullPhoneNumber);
            setVerificationId(verificationId);
        } catch (err: any) {
            setError(err.message || 'Telefon doğrulama kodu gönderilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        try {
            setLoading(true);
            setError(null);

            await authService.register({
                email: formData.email,
                password: formData.password,
                name: formData.name,
                phone: `${formData.countryCode}${formData.phone}`
            });

            // Clear reCAPTCHA after successful registration
            phoneAuthService.clearRecaptcha();
            console.log('reCAPTCHA cleared after successful registration');

            navigate('/profile');
        } catch (err: any) {
            setError(err.message || 'Kayıt sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <form onSubmit={handleFirstStep}>
                        <TextField
                            name="name"
                            label="Ad Soyad"
                            value={formData.name}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            name="email"
                            label="E-posta"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            name="password"
                            label="Şifre"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            sx={{ mt: 2 }}
                        >
                            Devam Et
                        </Button>
                    </form>
                );
            case 1:
                if (verificationId) {
                    return (
                        <PhoneVerification
                            verificationId={verificationId}
                            onSuccess={handlePhoneVerificationSuccess}
                            onError={(error) => setError(error)}
                        />
                    );
                }
                return (
                    <form onSubmit={handleSecondStep}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                {formData.isCustomCountry ? (
                                    <TextField
                                        label="Ülke Kodu"
                                        value={formData.countryCode}
                                        onChange={handleCustomCountryChange}
                                        fullWidth
                                        margin="normal"
                                        required
                                        placeholder="+XXX"
                                        inputProps={{ maxLength: 5 }}
                                        helperText="Örn: +994"
                                    />
                                ) : (
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Ülke Kodu</InputLabel>
                                        <Select
                                            value={formData.countryCode}
                                            onChange={handleCountryChange}
                                            label="Ülke Kodu"
                                        >
                                            {countryCodes.map((country) => (
                                                <MenuItem key={country.code} value={country.code}>
                                                    {country.code} ({country.country})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    name="phone"
                                    label="Telefon"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    required
                                    placeholder="5XX..."
                                    inputProps={{ maxLength: 10 }}
                                    helperText="Başında 0 olmadan 10 haneli numara"
                                />
                            </Grid>
                        </Grid>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isCustomCountry}
                                    onChange={toggleCustomCountry}
                                />
                            }
                            label="Ülkem listede yok, kendi ülke kodumu gireceğim"
                        />

                        <div 
                            id="recaptcha-container" 
                            ref={recaptchaContainerRef}
                            style={{ 
                                marginTop: '20px',
                                marginBottom: '20px',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            sx={{ mt: 2 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Doğrulama Kodu Gönder'}
                        </Button>
                    </form>
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h5" component="h1" gutterBottom align="center">
                    Kayıt Ol
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {renderStepContent(activeStep)}

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link href="/login" variant="body2">
                        Zaten hesabınız var mı? Giriş yapın
                    </Link>
                </Box>
            </Paper>
        </Container>
    );
}; 